<template>
  <div>
    <div v-if="selected_type == 2 && current_series.length && voltage_series.length">
      <LprGraph
        v-for="i in nPairs"
        :key="i"
        :voltageSeries="[voltage_series[i-1]]"
        :currentSeries="[current_series[i-1]]"
        :xMin="commonXRange?.min" :xMax="commonXRange?.max"
        :yMin="commonYRange?.min" :yMax="commonYRange?.max"
        :step="selected_recording?.dto?.lpr?.step"
        :scale="voltage_series[0].scale"
        :automaticReport="automaticReport"
        :ref="el => setGraphRef(el, i)"
        @img-export="dataUrl => onLprSvgExport(dataUrl, i)"
      />
    </div>
  </div>
</template>

<script>
import LprGraph from "@/components/global/indicators/LprGraph.vue";
import { BarChartData } from "@/api/barchartdata.js";
const Camur = require("@/utils/camurutils").default;

export default {
	components: { LprGraph },
	emits: ["lpr-images"],
	props: {
		selected_recording_type: { type: Number, required: true },
		selected_recording: { type: Object, required: true },
		selected_zone: { type: Object, required: true },
		//Automatic report props:
		automaticReport: { type: Boolean, default: false }
	},
	data() {
		return {
			lpr_data_api: null,
			lpr_chart_data: null,
			current_series: [],
			voltage_series: [],
			graphRefs: [],
      images: []
		};
	},

	created() {
    this.lpr_data_api = new BarChartData(this.$app, this.$appm.net);
		this.fetch_lpr_measurments_data();
  },

	watch: {
		"selected_recording"() {
			if (this.selected_recording) {
				if (this.selected_recording_type === 2) {
					this.fetch_lpr_measurments_data();
				}
			}
		},
		nPairs() {
      if (this.automaticReport) this.queueExportAll();
    }
	},
	
	methods: {
		setGraphRef(el, i) {
      if (!el) return;
      this.graphRefs[i - 1] = el;
    },

    onLprSvgExport(dataUrl, idx) {
			const i = idx - 1;
			this.images[i] = dataUrl;

			const got = this.images.filter(Boolean).length;
			if (got === this.nPairs) this.$emit("lpr-images", this.images.slice(0, this.nPairs));
		},

    queueExportAll() {
      this.$nextTick(() => {
        this.images = Array(this.nPairs).fill(null);
        for (let i = 0; i < this.nPairs; i++) {
          const child = this.graphRefs[i];
          if (child && typeof child.exportImg === "function") child.exportImg();
        }
      });
    },

		sortChannelsByCurrentOrVoltage() {
			const series = this.lpr_chart_data?.series;
			if (!Array.isArray(series)) return;

			this.current_series = [];
			this.voltage_series = [];

			for (const serie of series) {
				const ch   = this.channelById(serie.channelId);
				if (!ch) continue;
				// console.log("channel", ch)
				const nodeName = this.nodeByChannelId(ch.id).name
				const code  = String(ch.code || "").trim().toUpperCase();
				const unit  = ch?.formula?.unit || null;
				const scale = ch?.formula.scale;
				const decimals = ch?.formula.decimals;
				const label = ch?.name || ch?.code || `Ch ${ch?.id}`;

				const enriched = { ...serie, unit, scale, decimals, label, nodeName };

				if (code.startsWith("U")) this.voltage_series.push(enriched);
				else if (code.startsWith("I")) this.current_series.push(enriched);
			}
		},
		channelNameById(id) {
      for (const zone of [this.selected_zone]) {
        for (const node of zone.nodes) {
          for (const key in node.channels) {
            const ch = node.channels[key];
            if (ch.id === id) {
              const nodeName = node.name || `Node ${node.id}`;
              const chName = ch.name || ch.code || id;
              return `${nodeName}-${chName}`;
            }
          }
        }
      }
      return `Channel ${id}`;
    },

		channelById(id) {
			const zone = this.selected_zone;
			for (const node of zone.nodes) {
				for (const key in node.channels) {
					const ch = node.channels[key];
					if (ch.id === id) return ch;
				}
			}
			return null;
		},

		nodeByChannelId(id) {
			const zone = this.selected_zone;
			for (const node of zone.nodes) {
				for (const key in node.channels) {
					const ch = node.channels[key];
					if (ch.id === id) return node;
				}
			}
			return null;
		},

		async fetch_lpr_measurments_data() {
			if (!this.selected_recording) {
				return;
			}
			const selected = this.selected_recording;		

			const recording_start = selected
				? new Date(selected.start)
				: new Date(this.$app.settings.report.from_date);
			const recording_stop = selected
				? new Date(selected.stop)
				: new Date(this.$app.settings.report.to_date);

			let channel_ids = [];
			for  (let node of this.selected_zone.nodes) {
				for (let channelkey in node.channels) {
					let channel = node.channels[channelkey];
					// if (channel.userVisible) {
						if (channel.lastValueUpdated > "2020-01-01") {
							if (this.relevantChannel(channel, Camur.recording_type.rectype_lpr))
								channel_ids.push(channel.id);
						// }  
					}
				}
			}

			// console.log("channelids", channel_ids)

			if (channel_ids.length == 0) {
				return;
			}

			const resp = await this.lpr_data_api.fetch(
				recording_start,
				recording_stop,
				3600,
				this.$app.sltd.project.id,
				null,
				channel_ids,
				{
					recordingId: 0,
					recordingType: Camur.recording_type.rectype_lpr,
				},
				false,
				true,
				0
			);
			

			this.lpr_chart_data = resp;
			// console.log("lpr_chart_data snapshot",
			// 	JSON.parse(JSON.stringify(resp))
			// );
			this.sortChannelsByCurrentOrVoltage();
		},

		relevantChannel(channel, recordingType) {
			if (recordingType > 0) {
				switch (recordingType) {
					case Camur.recording_type.rectype_lpr_results:
						let node = this.project.find_node(channel.nodeId);
						if ((node.capability == Camur.node_type.ntype_corrowatch) || (node.capability == Camur.node_type.ntype_ladder)) {
							if (channel.no >= 24)
								return true;
							else
								return false;  
						} else if (node.capability == Camur.node_type.ntype_lpr) {
							if (channel.no >= 3)
								return true;
							else
								return false;  
						} else {
							return false;  
						}
					default:
						return true;
				}
			}
			else
				return true;
		},
	},
	computed: {
		commonYRange() {
			let min = Infinity, max = -Infinity;
			for (const s of this.voltage_series) {
				const vals = (s?.data || []).map(r => Array.isArray(r) ? r[2] : null).filter(Number.isFinite);
				if (!vals.length) continue;
				const base = vals[0];
				for (const v of vals) {
					const dv = v - base;
					if (dv < min) min = dv;
					if (dv > max) max = dv;
				}
			}
			if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
			if (min === max) { const pad = Math.abs(min || 1)*0.05; min -= pad; max += pad; }
			return { min, max };
		},
		commonXRange() {
			let min = Infinity, max = -Infinity;
			for (const s of this.current_series) {
				for (const row of (s?.data || [])) {
					const v = Array.isArray(row) ? row[2] : null;
					if (Number.isFinite(v)) { if (v < min) min = v; if (v > max) max = v; }
				}
			}
			if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
			if (min === max) { const pad = Math.abs(min || 1) * 0.05; min -= pad; max += pad; }
			return { min, max };
		},
		
		selected_type() {
			return this.selected_recording_type;
		},

		nPairs() { return Math.min(this.voltage_series.length, this.current_series.length); },
	},
};
</script>
